import React from 'react';

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import AssetsSchemaIcon from '@atlaskit/icon-lab/core/assets-schema';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';

import { ProductKeys, useProductConfig } from '../../../../../common/constants/products';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import { FilterButtonIconContainer } from '../../../../../common/ui/quick-find/filter-button/filter-button-icon-container';
import { FilterPopupSelect } from '../../../../../common/ui/quick-find/filter-popup-select';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store';
import { filter as assetsSchemaFilter } from '../../../../../controllers/store/search/filters/assets-schema';

import { i18n } from './messages';

export const AssetsSchemaFilter = () => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: assetsSchemaFilter.id,
	});

	const AssetsIcon = useProductConfig(ProductKeys.Assets).Icon;

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
		firstSelectedOptionLabel,
		badgeLabel,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.assetsFilterLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.assetsFilterLabelUnselected),
			options: suggestedOptions,
		},
	];

	const getIconBefore = () => {
		return (
			<FilterButtonIconContainer>
				<AssetsIcon size="xxsmall" />
				<AssetsSchemaIcon label={formatMessage(i18n.assetsIconLabel)} size="small" />
			</FilterButtonIconContainer>
		);
	};

	const getIconAfter = () => {
		return selectedOptions.length === 0 ? (
			<ChevronDownIcon label="" size="small" />
		) : (
			badgeLabel && <Badge appearance="primary">{badgeLabel}</Badge>
		);
	};

	return (
		<FilterPopupSelect
			filterId={assetsSchemaFilter.id}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={setOption}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={firstSelectedOptionLabel ? firstSelectedOptionLabel : formatMessage(i18n.assetsSchema)}
			placeholder={formatMessage(i18n.assetsFilterAssetsSchemaPlaceholder)}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			onClearOptions={clearOptions}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			target={({ isOpen, ...targetProps }) => (
				<FilterButton
					{...targetProps}
					isSelected={!!selectedOptions.length || isOpen}
					iconBefore={getIconBefore()}
					iconAfter={getIconAfter()}
				>
					{firstSelectedOptionLabel ? firstSelectedOptionLabel : formatMessage(i18n.assetsSchema)}
				</FilterButton>
			)}
		/>
	);
};

export default AssetsSchemaFilter;
